/**
 * Manages the lifecycle and interactions for the sorting drawer in the search results page.
 *
 * - Handles opening the sorting drawer when the associated button is clicked.
 * - Creates and configures a new SortingDrawer instance, dispatches its state to the Redux store,
 *   and binds/unbinds its events as the drawer is added or removed from the DOM.
 * - Listens for the drawer close event to clean up and reset the drawer state.
 *
 * This module ensures the sorting drawer is properly initialized, displayed, and cleaned up
 * in response to user actions and application state changes.
 */

import { filter, fromEvent } from 'rxjs';
import drawerService from 'layout/services/drawerService';
import { onDrawerClosed$, onOrientationChange$ } from 'layout/redux/observables';
import { store } from '@redux/store';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { ProductType, selectProductType, selectSorting, SortType } from 'features/searchResultsFacets';
import SortingDrawer from './sortingDrawer';

const sortingDrawerButton = document.querySelector('[data-sorting-drawer-button]');
const onSortingDrawerButtonClick$ = fromEvent(sortingDrawerButton, 'touchend');

const configuration = {
    drawerInstance: null,
};

/**
 * Creates and initializes a new SortingDrawer instance for the sorting drawer UI.
 *
 * - Instantiates a new SortingDrawer and stores the reference in the configuration object.
 * - Dispatches the drawer's information to the Redux store for UI state tracking.
 * - Registers event handlers for when the drawer's HTML is added to or removed from the DOM,
 *   binding and unbinding the drawer's events as appropriate.
 * - Injects the drawer's HTML into the DOM using the drawer service.
 *
 * This function is responsible for setting up the sorting drawer's lifecycle and ensuring
 * it is interactive and properly managed within the application's UI state.
 */
const createSortingDrawer = () => {
    const state = store.getState();
    const currentSort = SortType.fromKey(selectSorting(state)?.sortBy);

    configuration.drawerInstance = new SortingDrawer();
    configuration.drawerInstance.setMetadata({
        productType: ProductType.fromKey(selectProductType(state)),
        sorting: currentSort ? selectSorting(state) : null,
    });

    store.dispatch(uiStateSlice.actions.drawerInformation({
        name: configuration.drawerInstance.id,
    }));

    drawerService.onHtmlReady({
        name: configuration.drawerInstance.id,
        callback: configuration.drawerInstance.bindEvents.bind(configuration.drawerInstance),
    });

    drawerService.onHtmlRemoved({
        name: configuration.drawerInstance.id,
        callback: configuration.drawerInstance.unbindEvents.bind(configuration.drawerInstance),
    });

    drawerService.setHtml({ payload: configuration.drawerInstance.getHtml() });
};

/**
 * Cleans up and removes the current SortingDrawer instance from the configuration and Redux store.
 *
 * - Sets the drawerInstance reference in the configuration object to null.
 * - Dispatches an action to the Redux store to clear the drawer information from UI state.
 *
 * This function is typically called when the sorting drawer is closed, ensuring
 * that the application's state accurately reflects the absence of an active drawer.
 */
const onDrawerClosed = () => {
    configuration.drawerInstance = null;
    store.dispatch(uiStateSlice.actions.drawerInformation(null));
};

onDrawerClosed$.subscribe(onDrawerClosed);
onSortingDrawerButtonClick$.subscribe(createSortingDrawer);
onOrientationChange$.pipe(
  filter(() => configuration.drawerInstance !== null)
).subscribe(drawerService.clearHtml);